﻿import {hasFlag, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {IMvvmContextState, MvvmSelect, MvvmTextInput} from "@2d-front-monorepo/design-library";
import {CreateResidencyArguments, RequiredResidencyProperties} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useMemo} from "react";
import AttachmentType from "../enums/AttachmentType";
import ICreateComponentAttachments from "../interfaces/ICreateComponentAttachments";
import {IntegraZone} from "../types/IntegraZone";
import {ComponentWithChildrenParts} from "./ComponentParts";
import CreateAttachmentsComponent from "./CreateAttachmentsComponent";

export interface CreateResidencyComponentProps extends ComponentWithChildrenParts {
	context: IMvvmContextState<CreateResidencyModel>;
	zones: IntegraZone[];

	requiredProperties: RequiredResidencyProperties;
	readOnlyProperties?: RequiredResidencyProperties;
	hiddenProperties?: RequiredResidencyProperties;

	requiredAttachments?: AttachmentType[];
	optionalAttachments?: AttachmentType[];

	autoFocus?: boolean;
}

export type CreateResidencyModel = {
	arguments: CreateResidencyArguments;
	customData: Record<string, any>;
} & ICreateComponentAttachments;

export default function CreateResidencyComponent(props: CreateResidencyComponentProps): JSX.Element {
	const {
		context,
		zones,
		requiredProperties,
		readOnlyProperties,
		hiddenProperties,
		requiredAttachments,
		optionalAttachments,
		autoFocus,
		header,
		children,
		footer
	} = props;

	const roProperties = readOnlyProperties ? readOnlyProperties : RequiredResidencyProperties.None;
	const hProperties = hiddenProperties ? hiddenProperties : RequiredResidencyProperties.None;
	const zoneItems: StringNamedValue[] = useMemo(() => {
		return zones.map(z => {
			return {label: z.name, value: z.id};
		});
	}, [zones]);

	return (<div className="layout vertical gap">
		{header}
		<div className="columns gap">
			<MvvmTextInput context={context}
			               property="arguments.address.streetNumber"
			               type="text"
			               column="col25"
			               autoFocus={autoFocus}
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.StreetNumber)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.StreetNumber)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.StreetNumber) ? {required: translation(PermitTranslations.validation__residency__street_number__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__street_number)}/>

			<MvvmTextInput context={context}
			               property="arguments.address.street"
			               type="text"
			               column="col25"
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.Street)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.Street)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.Street) ? {required: translation(PermitTranslations.validation__residency__street__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__street)}/>

			<MvvmTextInput context={context}
			               property="arguments.address.unit.unit"
			               type="text"
			               column="col25"
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.Unit)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.Unit)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.Unit) ? {required: translation(PermitTranslations.validation__residency__apt_unit__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__apt_unit)}/>

			<MvvmTextInput context={context}
			               property="arguments.suffix"
			               type="text"
			               column="col25"
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.Suffix)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.Suffix)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.Suffix) ? {required: translation(PermitTranslations.validation__residency__suffix__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__suffix)}/>

			<MvvmTextInput context={context}
			               property="arguments.address.postalCode"
			               type="text"
			               column="col25"
			               autoComplete="postal-code"
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.PostalCode)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.PostalCode)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.PostalCode) ? {required: translation(PermitTranslations.validation__residency__postal_code__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__postal_code)}/>

			<MvvmTextInput context={context}
			               property="arguments.address.city"
			               type="text"
			               column="col25"
			               autoComplete="address-level2"
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.City)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.City)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.City) ? {required: translation(PermitTranslations.validation__residency__city__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__city)}/>

			<MvvmTextInput context={context}
			               property="arguments.address.state"
			               type="text"
			               column="col25"
			               autoComplete="address-level1"
			               hidden={hasFlag(hProperties, RequiredResidencyProperties.State)}
			               disabled={hasFlag(roProperties, RequiredResidencyProperties.State)}
			               validation={hasFlag(requiredProperties, RequiredResidencyProperties.State) ? {required: translation(PermitTranslations.validation__residency__state__required)} : undefined}
			               label={translation(PermitTranslations.new_residency__state)}/>

			<MvvmSelect context={context}
			            property="arguments.zoneId"
			            items={zoneItems}
			            column="col25"
			            hidden={hasFlag(hProperties, RequiredResidencyProperties.Zone)}
			            disabled={hasFlag(roProperties, RequiredResidencyProperties.Zone)}
			            validation={{required: translation(PermitTranslations.validation__residency__zone__required)}}
			            label={translation(PermitTranslations.zone)}/>
		</div>
		{children}
		<CreateAttachmentsComponent context={context}
		                            label={translation(PermitTranslations.documentation__required)}
		                            types={requiredAttachments}
		                            required={true}/>

		<CreateAttachmentsComponent context={context}
		                            label={translation(PermitTranslations.documentation__additional)}
		                            types={optionalAttachments}
		                            required={false}/>
		{footer}
	</div>);
}
